import { useMemo, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight, Clock, MapPin } from "lucide-react";
import { weddingConfig } from "../config/weddingConfig";

const EASE = [0.22, 1, 0.36, 1];

const DiamondDivider = () => (
  <div className="mt-7 flex items-center justify-center gap-3">
    <span className="h-px w-16 bg-gold/70" />
    <span className="rotate-45 border border-gold p-[3px]">
      <span className="block h-1 w-1 bg-gold" />
    </span>
    <span className="h-px w-16 bg-gold/70" />
  </div>
);

// Firecracker-style colour burst that fires once when an event page
// scrolls into view — palette comes from the event's `colors` in config.
const Burst = ({ colors }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-25% 0px" });

  const particles = useMemo(
    () =>
      Array.from({ length: 52 }, (_, i) => ({
        x: (Math.random() - 0.5) * 360,
        peak: -(Math.random() * 320 + 90),
        rotate: Math.random() * 540 - 270,
        scale: Math.random() * 0.8 + 0.6,
        delay: Math.random() * 0.2,
        color: colors[i % colors.length],
        round: i % 3 === 0,
      })),
    [colors],
  );

  return (
    <div
      ref={ref}
      className="pointer-events-none absolute inset-x-0 top-[32%] z-20 flex justify-center"
      aria-hidden="true"
    >
      {particles.map((p, i) => (
        <motion.span
          key={i}
          className={`absolute block ${
            p.round ? "h-2.5 w-2.5 rounded-full" : "h-3.5 w-2 rounded-[2px]"
          }`}
          style={{ backgroundColor: p.color }}
          initial={{ x: 0, y: 0, opacity: 0, scale: 0.2, rotate: 0 }}
          animate={
            inView
              ? {
                  x: [0, p.x * 0.6, p.x],
                  y: [0, p.peak, p.peak + 460],
                  opacity: [0, 1, 0],
                  rotate: [0, p.rotate * 0.6, p.rotate],
                  scale: [0.2, p.scale, p.scale * 0.8],
                }
              : {}
          }
          transition={{
            duration: 2.2,
            delay: p.delay + 0.15,
            ease: "easeOut",
            times: [0, 0.38, 1],
          }}
        />
      ))}
    </div>
  );
};

// Full-screen event page — the illustration runs edge-to-edge showing the
// entire outfit, melting into the navy at the tail and corners, with the
// details below the fade. A colour burst fires on arrival.
const EventPanel = ({ event }) => (
  <motion.article
    data-testid={`event-card-${event.id}`}
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, ease: EASE }}
    className="relative flex flex-col overflow-hidden pb-24 text-center"
  >
    {/* soft colour glow in the event's palette */}
    <div
      aria-hidden="true"
      className="absolute left-1/2 top-[30%] h-[64vmin] w-[64vmin] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-25 blur-3xl"
      style={{
        background: `radial-gradient(circle, ${event.colors[0]} 0%, transparent 70%)`,
      }}
    />

    <Burst colors={event.colors} />

    {/* full-width illustration showing the entire outfit, faded at the tail */}
    <div className="relative">
      <img
        src={event.image}
        alt={event.imageAlt}
        loading="lazy"
        decoding="async"
        data-testid={`event-avatar-${event.id}`}
        className="w-full [mask-image:linear-gradient(to_bottom,black_84%,transparent_100%)]"
      />
      <div
        className="absolute inset-0 bg-[radial-gradient(ellipse_130%_95%_at_50%_0%,transparent_55%,rgba(6,18,36,0.75)_100%)]"
        aria-hidden="true"
      />
    </div>

    {/* details tucked just under the faded tail of the artwork */}
    <div className="relative z-10 -mt-20 flex flex-col items-center px-6">
      <h3 className="font-serif text-3xl font-semibold uppercase tracking-wide text-cream sm:text-4xl">
        {event.name}
      </h3>

      <div
        className="mt-5 flex items-center justify-center gap-3"
        aria-hidden="true"
      >
        <span className="h-px w-14 bg-gold/60" />
        <span className="h-1.5 w-1.5 rotate-45 bg-gold" />
        <span className="h-px w-14 bg-gold/60" />
      </div>

      <p
        data-testid={`event-date-${event.id}`}
        className="mt-6 font-sans text-[15px] font-semibold uppercase tracking-[0.35em] text-gold-bright"
      >
        {event.date}
      </p>

      <div className="mt-4 flex flex-col items-center gap-3">
        <p className="flex items-center gap-2.5 font-sans text-xl text-cream/85">
          <Clock className="h-5 w-5 shrink-0 text-gold" />
          {event.time}
        </p>
        <p className="flex items-center gap-2.5 font-sans text-xl leading-snug text-cream/85">
          <MapPin className="h-5 w-5 shrink-0 text-gold" />
          {event.venue}
        </p>
      </div>
    </div>
  </motion.article>
);

export const Itinerary = () => {
  const { itinerary } = weddingConfig;

  return (
    <section
      id="itinerary"
      data-testid="itinerary-section"
      className="relative"
    >
      {/* header on cream */}
      <div className="bg-cream px-5 pb-20 pt-20 sm:pt-28">
        <div className="mx-auto max-w-4xl text-center">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            className="font-sans text-[11px] font-semibold uppercase tracking-[0.45em] text-gold-amber"
          >
            {itinerary.eyebrow}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="mt-4 font-serif text-3xl font-bold uppercase tracking-[0.18em] text-emerald-dark sm:text-4xl lg:text-5xl"
          >
            {itinerary.heading}
          </motion.h2>
          <DiamondDivider />

          <motion.a
            data-testid="venue-banner"
            href={itinerary.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
            className="mx-auto mt-9 inline-flex items-center gap-2.5 rounded-full border border-gold/50 bg-cream-card px-6 py-3 font-sans text-xs font-medium uppercase tracking-[0.2em] text-emerald-dark transition-colors duration-300 hover:border-gold hover:bg-emerald-dark hover:text-gold-bright focus:outline-none focus-visible:ring-2 focus-visible:ring-gold sm:text-sm"
          >
            <MapPin className="h-4 w-4 shrink-0 text-gold-amber" />
            {itinerary.venueNote}
            <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-gold-amber" />
          </motion.a>

          <motion.p
            data-testid="venue-banner-hint"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="mt-3.5 font-sans text-[9px] font-medium uppercase tracking-[0.35em] text-gold-amber/80"
          >
            {itinerary.venueHint}
          </motion.p>
        </div>
      </div>

      {/* each event takes over a full screen */}
      <div className="bg-emerald-deep">
        {itinerary.events.map((event) => (
          <EventPanel key={event.id} event={event} />
        ))}
      </div>
    </section>
  );
};
